import TextField from '@/components/TextField/TextField';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, AutocompleteRenderInputParams, Chip, SxProps } from '@mui/material';
import { produce } from 'immer';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { SyntheticEvent } from 'react';
import { Divider } from '../common';

const SUGGESTED_TOPICS = ['Pricing', 'Competitors', 'Legal', 'Compliance Matter', 'Third-Party Integrations'];

const ChipContainer = styled.div`
    display: flex;
    flex-direction: row;

    gap: 8px;
    padding-top: 8px;

    overflow-x: auto;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer 10+ */

    &::-webkit-scrollbar {
        display: none; /* Chrome, Safari, Opera */
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;

    width: 100%;
    padding: 8px;

    border: 1px solid ${theme.colors.grey.border};
    border-radius: 8px;
`;

const StyledTextField = styled(TextField)`
    & .MuiOutlinedInput-root {
        padding: 0;
        overflow-x: auto;
        flex-wrap: wrap;

        fieldset {
            border: none;
        }
    }

    & .MuiOutlinedInput-input {
        min-width: 100px !important;
        font-size: 14px;
        padding: 2px 4px !important;
    }
`;

const StyledDivider = styled(Divider)`
    color: ${theme.colors.grey.border};
    margin-top: 12px;
`;

const StyledChip = styled(Chip)`
    color: ${theme.colors.secondary.main};
    background-color: ${theme.colors.secondary[100]};
    height: 24px;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
    letter-spacing: 0.15px;

    :hover {
        background-color: ${theme.colors.secondary[200]};
    }
`;

const AutocompleteChipSx: SxProps = {
    backgroundColor: 'white',
    border: `1px solid ${theme.colors.black[300]}`,
    color: theme.colors.secondary.main,
    height: '24px',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: '16px',
    letterSpacing: '0.15px',

    '&:hover': {
        cursor: 'pointer',
        backgroundColor: theme.colors.black[100],
    },

    '& .MuiChip-deleteIcon': {
        color: theme.colors.secondary.main,
        height: '16px',
        width: '16px',
        '&:hover': {
            color: theme.colors.secondary.main,
        },
    },
};

export const TopicsToAvoid = observer(() => {
    const agentEditorStore = useAgentEditorStore();

    const fieldValue = agentEditorStore.llm.prompt_customization?.topics_to_avoid || [];

    const setFieldValue = (value: string[]) => {
        agentEditorStore.setLLM(
            produce(toJS(agentEditorStore.llm), llm => {
                (llm.prompt_customization ??= {}).topics_to_avoid = value.map(x => x.trim()).filter(Boolean);
            })
        );
    };

    const onChipClick = (topic: string) => () => {
        setFieldValue([...fieldValue, topic]);
    };

    const handleValueChange = (_: SyntheticEvent, value: string[]) => {
        setFieldValue(value);
    };

    const filteredSuggestedTopics = SUGGESTED_TOPICS.filter(topic => !fieldValue.includes(topic));

    return (
        <Container>
            <Autocomplete
                multiple
                freeSolo
                disableClearable
                options={[]}
                value={fieldValue}
                onChange={handleValueChange}
                renderInput={(params: AutocompleteRenderInputParams) => {
                    return <StyledTextField {...params} placeholder="Type a topic..." />;
                }}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            variant="outlined"
                            label={option}
                            {...getTagProps({ index })}
                            sx={AutocompleteChipSx}
                            deleteIcon={<CloseIcon />}
                        />
                    ))
                }
            />

            {!!filteredSuggestedTopics.length && (
                <>
                    <StyledDivider />
                    <ChipContainer>
                        {filteredSuggestedTopics.map(topic => (
                            <StyledChip key={topic} label={topic} onClick={onChipClick(topic)} />
                        ))}
                    </ChipContainer>
                </>
            )}
        </Container>
    );
});
